import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Expand } from "lucide-react";
import { sized, srcSetOf, altOf } from "./media";
import { NoPhoto, ProductTile } from "../NoPhoto";

const Lightbox = lazy(() => import("./Lightbox"));

const Placeholder = ({ name, ph }) => <ProductTile size="lg" {...ph} name={name} nophotoTestid="gallery-placeholder" />;

// T64: thumbnail rail without native scrollbars: fades + up/down arrows show there is more (wheel / touch / keyboard still scroll)
function RailEdges({ rail, ends }) {
  const by = (d) => rail.current?.scrollBy({ top: d * 160, behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
  const btn = "absolute left-1/2 z-10 flex h-7 w-7 -translate-x-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm hover:text-[var(--brand-primary)]";
  return (<>
    {!ends.top && <><span aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-white to-transparent" data-testid="gallery-rail-fade-top" />
      <button type="button" tabIndex={-1} aria-label="Scroll thumbnails up" onClick={() => by(-1)} className={`${btn} top-1`} data-testid="gallery-rail-up"><ChevronUp size={15} aria-hidden="true" /></button></>}
    {!ends.bottom && <><span aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white to-transparent" data-testid="gallery-rail-fade-bottom" />
      <button type="button" tabIndex={-1} aria-label="Scroll thumbnails down" onClick={() => by(1)} className={`${btn} bottom-1`} data-testid="gallery-rail-down"><ChevronDown size={15} aria-hidden="true" /></button></>}
  </>);
}

export const Gallery = ({ images, name, ph }) => {
  const rail = useRef(null);
  const [ends, setEnds] = useState({ top: true, bottom: true });
  const edge = useCallback(() => { const r = rail.current; if (r) setEnds({ top: r.scrollTop <= 2, bottom: r.scrollTop + r.clientHeight >= r.scrollHeight - 2 }); }, []);
  useEffect(() => {
    const r = rail.current;
    if (!r) return undefined;
    edge();
    const ro = new ResizeObserver(edge);
    ro.observe(r);
    return () => ro.disconnect();
  }, [edge, images]);
  const n = images.length;
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(null);
  const [failed, setFailed] = useState({});
  const [box, setBox] = useState(null);
  const strip = useRef(null);
  const opener = useRef(null);

  const go = useCallback((i) => {
    const next = (i + n) % n;
    setIdx(next);
    const el = strip.current;
    if (el) el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
  }, [n]);

  useEffect(() => { setIdx(0); strip.current?.scrollTo({ left: 0 }); }, [images]);

  if (!n) return <Placeholder name={name} ph={ph} />;

  const onScroll = (e) => {
    const el = e.currentTarget;
    const i = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
    if (i !== idx) setIdx(i);
  };
  const onKey = (e) => {
    if (n < 2) return;
    if (e.key === "ArrowRight") { e.preventDefault(); go(idx + 1); }
    if (e.key === "ArrowLeft") { e.preventDefault(); go(idx - 1); }
  };
  const onMove = (e) => {
    if (e.pointerType !== "mouse") return;
    const r = e.currentTarget.getBoundingClientRect();
    setZoom({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
  };
  const open = (i, el) => { opener.current = el; setBox(i); };

  return (
    <div className="min-w-0 self-start md:grid md:grid-cols-[76px_minmax(0,1fr)] md:gap-4" data-testid="product-gallery" onKeyDown={onKey}>
      {n > 1 && (
        <div className="relative hidden md:block">
        <div ref={rail} onScroll={edge} className="absolute inset-0 flex flex-col gap-2 overflow-y-auto overflow-x-hidden overscroll-contain p-[3px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist" aria-label="Product images" data-testid="gallery-rail">
          {images.map((src, i) => (
            <button key={src + i} type="button" role="tab" aria-selected={i === idx} aria-label={`Show image ${i + 1} of ${n}`}
              data-testid={`gallery-thumb-${i}`} onClick={() => go(i)} onMouseEnter={() => go(i)} onFocus={(e) => e.currentTarget.scrollIntoView({ block: "nearest" })}
              className={`h-[72px] w-[72px] shrink-0 overflow-hidden rounded-xl border-2 bg-white transition-colors ${i === idx ? "border-[var(--brand-accent)]" : "border-slate-200 hover:border-slate-400"}`}>
              <img src={sized(src, 160)} width="72" height="72" loading="lazy" decoding="async" alt="" className="h-full w-full object-contain p-1.5" />
            </button>
          ))}
        </div>
        <RailEdges rail={rail} ends={ends} />
        </div>
      )}
      <div className={`group relative min-w-0 self-start ${n > 1 ? "" : "md:col-span-2"}`}>
        <div ref={strip} onScroll={onScroll} tabIndex={0} aria-roledescription="carousel" aria-label={`${name} images`}
          className="flex aspect-square snap-x snap-mandatory overflow-x-auto overflow-y-hidden rounded-2xl border border-slate-200 bg-white [scrollbar-width:none] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-focus)] md:overflow-hidden [&::-webkit-scrollbar]:hidden"
          data-testid="gallery-main">
          {images.map((src, i) => (
            <button key={src + i} type="button" onClick={(e) => open(i, e.currentTarget)} aria-label={`Open image ${i + 1} of ${n} full screen`}
              onPointerMove={i === idx ? onMove : undefined} onPointerLeave={() => setZoom(null)}
              className="relative h-full w-full shrink-0 snap-center cursor-zoom-in overflow-hidden" data-testid={`gallery-slide-${i}`}>
              {failed[i] ? <NoPhoto name={name} {...ph} size="lg" testid="gallery-placeholder" /> : <img src={sized(src, 640)} srcSet={srcSetOf(src)} sizes="(min-width: 1024px) 560px, (min-width: 768px) 45vw, 100vw"
                width="640" height="640" alt={altOf(name, i, n)} decoding="async"
                loading={i === 0 ? "eager" : "lazy"} fetchPriority={i === 0 ? "high" : "low"}
                className="h-full w-full object-contain p-[6%] transition-transform duration-150 ease-out motion-reduce:transition-none"
                style={zoom && i === idx ? { transform: "scale(2.2)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : undefined}
                onError={() => setFailed((f) => ({ ...f, [i]: true }))}
                data-testid={i === 0 ? "product-main-image" : undefined} />}
            </button>
          ))}
        </div>
        {n > 1 && (
          <>
            <span className="pointer-events-none absolute left-3 top-3 rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold tabular-nums text-slate-700 shadow-sm" data-testid="gallery-counter" aria-live="polite">{idx + 1} / {n}</span>
            {[["prev", -1, ChevronLeft, "left-3"], ["next", 1, ChevronRight, "right-3"]].map(([k, d, Icon, pos]) => (
              <button key={k} type="button" onClick={() => go(idx + d)} aria-label={k === "prev" ? "Previous image" : "Next image"} data-testid={`gallery-${k}`}
                className={`absolute ${pos} top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-700 opacity-0 shadow-md transition-opacity hover:text-[var(--brand-primary)] focus-visible:opacity-100 group-hover:opacity-100 md:flex`}>
                <Icon size={20} aria-hidden="true" />
              </button>
            ))}
            <div className="mt-3 flex justify-center gap-1.5 md:hidden" data-testid="gallery-dots" aria-hidden="true">
              {images.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-[var(--brand-accent)]" : "w-1.5 bg-slate-300"}`} />)}
            </div>
          </>
        )}
        <button type="button" onClick={(e) => open(idx, e.currentTarget)} aria-label="View images full screen" data-testid="gallery-expand"
          className="absolute bottom-3 right-3 flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white/95 text-slate-700 shadow-sm transition-colors hover:text-[var(--brand-primary)]">
          <Expand size={18} aria-hidden="true" />
        </button>
      </div>
      {box !== null && (
        <Suspense fallback={null}>
          <Lightbox images={images} name={name} start={box} onClose={(i) => { setBox(null); go(i); setTimeout(() => opener.current?.focus(), 0); }} />
        </Suspense>
      )}
    </div>
  );
};
